/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import _ from 'lodash'
import React, {useEffect, useMemo} from 'react'
import {useIntl} from 'react-intl'

import {
    Box,
    Divider,
    Flex,
    Image,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

import {useGetFooter} from '../../clients/contentful/hooks'
import {languageAliases, styleConfigChakraKeys} from '../../constants'
import LinksList from '../links-list'
import FooterMeta from './footer-meta'
import {contentfulMediaFormatter} from '../../utils'

const Footer = ({...otherProps}) => {
    const {locale} = useIntl()
    const {data} = useGetFooter()
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    const styles = useMultiStyleConfig(styleConfigChakraKeys.footer)

    const rightAsset = useMemo(() => {
        if (data?.mediaAsset) {
            const asset = contentfulMediaFormatter(data?.mediaAsset)
            return !isDesktop && asset?.mobile?.url
                ? asset?.mobile
                : asset?.desktop
        }

        return null
    }, [data])

    useEffect(() => {
        if (locale) {
            const script = document.createElement('script')
            script.src = 'https://acsbapp.com/apps/app/dist/js/app.js'
            script.async = true
            script.onload = () => {
                if (window.acsbJS) {
                    window.acsbJS.init({
                        statementLink: '',
                        footerHtml: '',
                        hideMobile: true,
                        hideTrigger: true, // Floating trigger is visible
                        disableBgProcess: false,
                        language:
                            _.find(
                                languageAliases,
                                (alias) => alias.locale === locale
                            )?.localeName || 'en'
                    })
                }
            }

            const headOrBody = document.querySelector('head') || document.body
            headOrBody.appendChild(script)

            return () => {
                try {
                    headOrBody.removeChild(script) // Clean up script on unmount
                } catch (error) {
                    console.log(error)
                }
            }
        }
    }, [locale])

    if (!data) return <></>

    return (
        <Flex
            {...styles.container}
            as="footer"
            id="footer"
            {...otherProps}
            width="100%"
            marginTop="20px"
            flexFlow="column"
        >
            <Flex
                width="100%"
                padding={{base: '24px', xl: '40px'}}
                flexFlow={{base: 'column-reverse', xl: 'row'}}
                gap={{base: '24px', xl: 'unset'}}
            >
                <Flex
                    flex="1"
                    gap={{base: '5px', xl: 'unset'}}
                    flexFlow="row"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    paddingRight={{base: '0', xl: '70px'}}
                >
                    {data?.lists?.map((item, index) => (
                        <LinksList
                            key={`${item?.contentfulTitle}-${index}`}
                            heading={item?.heading}
                            links={item?.items ?? []}
                        />
                    ))}
                </Flex>

                {rightAsset && (
                    <Box {...styles.footerImageContainer} flex="1">
                        <Image
                            {...styles.image}
                            borderRadius="24px"
                            data-testid={'footer-banner'}
                            src={rightAsset?.url}
                            ignoreFallback={true}
                            alt={rightAsset?.url}
                        />
                    </Box>
                )}
            </Flex>
            {data?.copyright && (
                <>
                    {isDesktop && <Divider {...styles.divider} />}
                    <Flex flexFlow="column">
                        <FooterMeta
                            copyright={data?.copyright}
                            bottomActions={data?.bottomActions ?? []}
                            socialMediaIcons={data?.socialMediaIcons ?? []}
                        />
                    </Flex>
                </>
            )}
        </Flex>
    )
}

export default Footer
